/**
 * Долг: лимит, пеня, ступени, эффективный баланс.
 *
 * Назначение файла: §Э6.7 в коде. Долг — это единственный способ, которым игрок уходит
 * «в минус»: сам счёт ☾ ниже нуля не опускается, вместо этого растёт отдельное поле debt.
 * Такое разделение позволяет держать инвариант `hive >= 0` и при этом честно показывать
 * игроку, сколько он задолжал улью (docs/economy/05-debt.md).
 *
 * Импорты: константы долга, помощники арифметики, типы кошелька.
 *
 * Источник: docs/economy/05-debt.md, docs/economy/06-formulas.md §Э6.7
 */

import { DEBT } from "./constants.js";
import { guaranteedDailyRecoveryMilli } from "./awards.js";
import { floorMilli } from "./math.js";
import {
  crystalsToMilli,
  type DebtTier,
  type Milli,
  type PlayerState,
  type Wallet,
} from "./types.js";

/** Лимит долга в milli: 50 ☾ плюс 15 ☾ за уровень (Э6.7). */
export function debtLimitMilli(level: number): Milli {
  if (!Number.isInteger(level) || level < 1) {
    throw new Error(`Уровень должен быть целым от 1, получено ${level}`);
  }
  return crystalsToMilli(DEBT.limitBase + DEBT.limitPerLevel * level);
}

/** Ставка суточной пени: после половины лимита растёт (Э6.7). */
export function penaltyRate(wallet: Wallet, level: number): number {
  const limit = debtLimitMilli(level);
  return wallet.debt < DEBT.shadowShare * limit ? DEBT.penaltyRateLow : DEBT.penaltyRateHigh;
}

/**
 * Суточная пеня в milli (Э6.7). Уходит в burn, никому не начисляется.
 * Пеня не может перебросить долг через лимит: остаток обрезается — иначе долг рос бы
 * бесконечно и игрок не смог бы выбраться никогда.
 */
export function dailyPenalty(wallet: Wallet, level: number): Milli {
  const limit = debtLimitMilli(level);
  const byRate = floorMilli(wallet.debt * penaltyRate(wallet, level));
  return Math.max(0, Math.min(byRate, limit - wallet.debt));
}

/**
 * Ступень долга (Э6.7).
 * `arrears` — недоимка, игра почти не ограничена;
 * `shadow` — Тень долга: доход режется, осады запрещены;
 * `arrest` — Арест улья: принудительные торги сотами.
 */
export function debtTier(wallet: Wallet, level: number): DebtTier {
  const limit = debtLimitMilli(level);
  if (wallet.debt <= 0) return "none";
  if (wallet.debt >= limit) return "arrest";
  if (wallet.debt >= DEBT.shadowShare * limit) return "shadow";
  return "arrears";
}

/**
 * Эффективный баланс: сколько у игрока «на руках» с учётом долга.
 * Купленные ✦ в расчёт не входят намеренно: они неприкосновенны, и долг за них
 * не гасится автоматически (docs/economy/01-currencies.md).
 */
export function effectiveBalance(wallet: Wallet): Milli {
  return wallet.hive - wallet.held - wallet.debt;
}

/** Свободные к трате ☾: за вычетом замороженного под осады. */
export function spendableMilli(wallet: Wallet): Milli {
  return Math.max(0, wallet.hive - wallet.held);
}

/**
 * Начисление долга с обрезкой по лимиту.
 * Возвращает сколько удалось записать в долг и сколько «сгорело» — вторая часть
 * не начисляется никому: недоимка сверх лимита просто не существует (Э6.5, правило недоимки).
 */
export function addDebt(
  wallet: Wallet,
  amount: Milli,
  level: number,
): { debt: Milli; recorded: Milli; ignored: Milli } {
  if (amount < 0) throw new Error("Начисляемый долг не может быть отрицательным");
  const limit = debtLimitMilli(level);
  const room = Math.max(0, limit - wallet.debt);
  const recorded = Math.min(amount, room);
  return { debt: wallet.debt + recorded, recorded, ignored: amount - recorded };
}

/**
 * Погашение долга поступлением. Любой приход ☾ сначала гасит долг, и только остаток
 * попадает на счёт — иначе игрок мог бы копить кристаллы, оставаясь в Тени долга
 * (docs/economy/05-debt.md).
 */
export function applyIncomeToDebt(
  wallet: Wallet,
  income: Milli,
): { debt: Milli; toWallet: Milli; repaid: Milli } {
  if (income < 0) throw new Error("Поступление не может быть отрицательным");
  const repaid = Math.min(wallet.debt, income);
  return { debt: wallet.debt - repaid, toWallet: income - repaid, repaid };
}

/** Списание за принудительные торги при аресте: трое суток дохода соты (Э6.7). */
export function auctionCharge(hexDailyIncomeMilli: Milli): Milli {
  if (hexDailyIncomeMilli < 0) throw new Error("Доход соты не может быть отрицательным");
  return hexDailyIncomeMilli * DEBT.auctionChargeDays;
}

/** Запрещены ли осады из-за долга: с половины лимита нападать нельзя (Э6.6). */
export function debtBlocksSiege(player: PlayerState): boolean {
  const limit = debtLimitMilli(player.level);
  return player.wallet.debt >= DEBT.shadowShare * limit;
}

/**
 * Прогноз суток до полного погашения долга гарантированным минимумом (§Т2.4 GET /wallet).
 * Без долга — 0. Если минимум нулевой — 0 (игрок не выйдет без действий).
 */
export function recoveryDaysLeft(player: PlayerState): number {
  if (player.wallet.debt <= 0) return 0;
  const daily = guaranteedDailyRecoveryMilli(player);
  if (daily <= 0) return 0;
  return Math.ceil(player.wallet.debt / daily);
}
